import { Base64 } from 'js-base64';

import padStart from 'lodash/padStart';
import sample from 'lodash/sample';
import qs from 'qs';

import { lang } from '@ott/l10n';
import {
  parseServerDateUTC,
  diff as getDaysDiff,
  add,
  cleanUTCTime,
  stringifyDateReverse,
  stringifyDateUTC,
} from '@ott/utility-date';

import { PRODUCTS } from '../constants';

let hostname = '';

if (__DEV__) {
  hostname = 'https://www.onetwotrip.com';
}

const getHrefByProduct = {
  [PRODUCTS.avia]: (route, productLang) => `${hostname}/${productLang}/f/${route}`,
  [PRODUCTS.poezda]: (route, productLang) => `${hostname}/${productLang}/poezda/${route}`,
  [PRODUCTS.hotels]: (route, productLang) => `${hostname}/${productLang}/hotels/${route}`,
  [PRODUCTS.bundles]: (route, productLang) =>
    `${hostname}/${productLang}/packages/#/search${route}`,
  [PRODUCTS.bus]: (route, productLang) => `${hostname}/${productLang}/bus/${route}`,
  [PRODUCTS.cars]: (route, productLang) => `${hostname}/${productLang}/cars/${route}`,
};
const getBackgroundImageUrlByIata = (code) => `//static.onetwotrip.com/deals/cities/${code}.jpg`;

const RAILWAYS_BANNER_URL = `/ott-static/images/railwaysIndexBanner/`;

const getNightsQty = (dateStart, dateEnd) => {
  if (dateStart && dateEnd) {
    return Math.max(1, getDaysDiff(dateStart, dateEnd, 'days'));
  }

  return 1;
};

const getMapHotelDeal =
  (getHref) =>
  (deal = {}) => {
    const chekinUTCDate = parseServerDateUTC(deal.date_start.replace(/-/g, ''));
    const checkoutUTCDate = parseServerDateUTC(deal.date_end.replace(/-/g, ''));
    const nights = getNightsQty(chekinUTCDate, checkoutUTCDate);
    const route = deal.url.split('/hotels/')[1];

    return {
      route,
      href: getHref(route, lang),
      backgroundImageUrl: deal.images[0].url,
      cityName: deal.object.name || deal.alias,
      countryName: deal.object.country.name,
      price: deal.price.value,
      currency: deal.price.currency,
      nights,
    };
  };

const getStaticRailwaysDeal = (getHref) => (deal) => {
  const date = add(new Date(), 7, 'days');

  return {
    from: deal.from,
    to: deal.to,
    href: getHref(`${deal.route}&date=${stringifyDateReverse(date)}`, lang),
    route: deal.route,
    price: deal.price,
    currency: deal.currency,
    backgroundImageUrl: `${RAILWAYS_BANNER_URL}${deal.name}@2x.jpg`,
  };
};

function mapDynamicRailwaysDeal(deal) {
  const imageUrl =
    deal.picture && !deal.picture.startsWith('http') ? `https://${deal.picture}` : deal.picture;

  const now = cleanUTCTime(new Date(Date.now()));
  const to = add(now, 90, 'days');
  const time = new Date(deal.showDateTo).getTime();

  if (now > time || to < time) {
    return null;
  }

  return {
    from: deal.cityFrom,
    to: deal.cityTo,
    dates: {
      start: new Date(deal.showDateFrom),
      end: new Date(deal.showDateTo),
    },
    href: deal.link,
    price: deal.priceNew,
    train: `${deal.trainNumber} / ${deal.carType}`,
    backgroundImageUrl: imageUrl,
  };
}

function mapDynamicRailwaysDeals(rawDeals) {
  return rawDeals.map(mapDynamicRailwaysDeal).filter(Boolean);
}

const getMapAviaDeal =
  (getHref) =>
  (deal = {}) => {
    const { from, to, departDate, returnDate, to_string: toString, currency } = deal;

    const backgroundImageUrl = getBackgroundImageUrlByIata(to);
    const title = toString || to;

    let route = `search/${departDate.substr(6)}${departDate.substr(4, 2)}${from}${to}`;
    let isRoundTrip = false;

    if (returnDate) {
      route += `${returnDate.substr(6)}${returnDate.substr(4, 2)}`;
      isRoundTrip = true;
    }
    const href = getHref(route, lang);

    return {
      href,
      route,
      backgroundImageUrl,
      title,
      dates: {
        start: parseServerDateUTC(departDate),
        end: returnDate && parseServerDateUTC(returnDate),
      },
      price: deal.price,
      isRoundTrip,
      currency,
    };
  };

const getMapCarsDeal = (getHref) => (deal) => {
  const pDate = add(new Date(), 3, 'days');
  const dDate = add(new Date(), 5, 'days');

  const searchParams = {
    pDate: stringifyDateUTC(pDate),
    dDate: stringifyDateUTC(dDate),
    pTime: '1200',
    dTime: '1200',
    pKey: deal.pKey,
    age: 30,
    lang,
    currency: 'RUB',
    pLocation: Base64.encode(
      JSON.stringify({
        name: deal.name,
        country: deal.country,
        key: deal.pKey,
        id: deal.iata,
        code: deal.iata,
        city: deal.name,
        type: 'city',
      })
    ),
  };

  const route = `search?${qs.stringify(searchParams)}`;

  return {
    ...deal,
    href: getHref(route, lang),
    route,
    backgroundImageUrl: getBackgroundImageUrlByIata(deal.iata),
    dates: {
      start: pDate,
      end: dDate,
    },
    price: deal.price,
    cityName: deal.name,
    countryName: deal.country,
    currency: 'RUB',
  };
};

const getMapBusDeal = (getHref) => (deal) => {
  const stringifyDate = (date) => {
    const day = padStart(date.getUTCDate(), 2, '0');
    const month = padStart(date.getUTCMonth() + 1, 2, '0');
    const year = date.getUTCFullYear();

    return `${year}-${month}-${day}`;
  };

  const DATE_SEARCH_KEY = 'date';

  const tomorrow = add(new Date(), 1, 'days');

  const backgroundImageUrl = getBackgroundImageUrlByIata(deal.cityCode);

  const historyRoute = `${deal.route}&${[DATE_SEARCH_KEY]}=${stringifyDate(tomorrow)}`;

  return {
    ...deal,
    backgroundImageUrl,
    historyRoute,
    href: getHref(historyRoute, lang),
  };
};

// eslint-disable-next-line no-bitwise
const toPercent = (data) => (data * 100) ^ 0;

const mapBundlesDeals = (rawData, getHref) => {
  const SEARCH_KEYS = {
    ADULTS: 'adults',
    CHILDREN: 'children',
    CHILDREN_AGES: 'children_ages',
    DATE_START: 'date_start',
    DATE_END: 'date_end',
    CURRENCY: 'currency',
    LANG: 'lang',
    LOCALE: 'locale',
    AVIA_SERVICE_CLASS: 'cs',
    AVIA_ONE_WAY: 'ow',
    FROM_CODE: 'fromCode',
    TO: 'to',
    TO_ID: 'id',
    TO_TYPE: 'type',
    TO_LAT: 'lat',
    TO_LNG: 'lng',
    TO_COUNTRY_CODE: 'country_code',
    TO_AIRPORTS: 'airports',
  };

  const byStars = (a, b) => a.hotel.stars - b.hotel.stars;
  const byPrice = (a, b) => a.bundle.price - b.bundle.price;

  const getIdsBestDeals = (deals) => {
    return deals.map((group) => {
      const dealsByStars = {};

      group.bundleDeals.forEach((deal) => {
        const stars = deal.hotel.stars;

        if (!dealsByStars[stars]) {
          dealsByStars[stars] = [];
        }
        dealsByStars[stars].push(deal);
      });

      return Object.keys(dealsByStars)
        .map((stars) => dealsByStars[stars].sort(byPrice)[0])
        .sort(byStars)
        .map((deal) => deal.hotel.id);
    });
  };

  const getDirectionsByIds = (directions) =>
    directions.reduce((res, dir) => {
      // eslint-disable-next-line no-param-reassign
      res[dir.id] = dir;

      return res;
    }, {});

  const searchQueryAdapter = (query, direction) => ({
    [SEARCH_KEYS.FROM_CODE]: query.from,
    [SEARCH_KEYS.TO_AIRPORTS]: query.airports,
    [SEARCH_KEYS.ADULTS]: query.adults,
    [SEARCH_KEYS.CHILDREN]: query.children,
    [SEARCH_KEYS.CHILDREN_AGES]: query.children_ages || [],
    [SEARCH_KEYS.DATE_START]: query.date_start,
    [SEARCH_KEYS.DATE_END]: query.date_end,
    [SEARCH_KEYS.TO]: direction.name,
    [SEARCH_KEYS.TO_ID]: query.object_id,
    [SEARCH_KEYS.TO_TYPE]: query.object_type,
    [SEARCH_KEYS.TO_LAT]: direction.lat,
    [SEARCH_KEYS.TO_LNG]: direction.lng,
    [SEARCH_KEYS.TO_COUNTRY_CODE]: direction.country_code,
  });

  const getDealsById = (deals, directionsByIds) =>
    deals.reduce((res, group) => {
      group.bundleDeals.forEach((deal) => {
        const direction = directionsByIds[group.searchQuery.object_id];
        const searchQuery = searchQueryAdapter(group.searchQuery, direction);

        // eslint-disable-next-line no-param-reassign
        res[deal.hotel.id] = {
          ...deal,
          country: direction.country,
          searchQuery,
        };
      });

      return res;
    }, {});

  const dealsById = getDealsById(rawData.deals.result, getDirectionsByIds(rawData.geo.result));
  const topByStarAndPriceDeals = getIdsBestDeals(rawData.deals.result);
  const bestDeals = topByStarAndPriceDeals.map((items) => sample(items));

  const mapBundleDeal = (deal) => {
    const route = qs.stringify(deal.searchQuery, { addQueryPrefix: true });
    const href = getHref(route, lang);

    const prevPrice = deal.bundle.discount + deal.bundle.price;

    return {
      href,
      backgroundImageUrl: getBackgroundImageUrlByIata(deal.searchQuery.airports),
      dates: {
        start: parseServerDateUTC(deal.searchQuery.date_start.replace(/-/g, '')),
        end: parseServerDateUTC(deal.searchQuery.date_end.replace(/-/g, '')),
      },
      country: deal.country,
      price: deal.bundle.price,
      currency: deal.bundle.currency,
      prevPrice,
      discountPercent: toPercent(deal.bundle.discount / prevPrice),
      hotelStars: deal.hotel.stars,
      hotelName: deal.hotel.translated_name || deal.hotel.name,
      cityName: deal.hotel.city_name_translated || deal.hotel.city,
    };
  };

  return bestDeals.map((id) => dealsById[id]).map(mapBundleDeal);
};

export function prepareDeals(data, product, getHref, isStaticDeals) {
  const getDealHref = getHref || getHrefByProduct[product];

  switch (product) {
    case PRODUCTS.poezda:
      if (isStaticDeals) {
        return data.map(getStaticRailwaysDeal(getDealHref));
      }

      return mapDynamicRailwaysDeals(data.result);
    case PRODUCTS.hotels:
      return data.result.deals.map(getMapHotelDeal(getDealHref));
    case PRODUCTS.avia:
      return data.map(getMapAviaDeal(getDealHref));
    case PRODUCTS.bus:
      return data.map(getMapBusDeal(getDealHref));
    case PRODUCTS.bundles:
      return mapBundlesDeals(data, getDealHref);
    case PRODUCTS.cars:
      return data.map(getMapCarsDeal(getDealHref));
    default:
      return [];
  }
}
